/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as DeveloperOptionsActionCreators from '@app/actions/DeveloperOptionsActionCreators';
import * as UserSettingsActionCreators from '@app/actions/UserSettingsActionCreators';
import {Input} from '@app/components/form/Input';
import {Switch} from '@app/components/form/Switch';
import {getToggleGroups} from '@app/components/modals/tabs/developer_options_tab/DeveloperOptionsToggleGroups';
import styles from '@app/components/modals/tabs/developer_options_tab/GeneralTab.module.css';
import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import UserSettingsStore from '@app/stores/UserSettingsStore';
import UserStore from '@app/stores/UserStore';
import {isIMEComposing} from '@app/utils/IMECompositionUtils';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useState} from 'react';

function formatBypassUserIds(value: ReadonlyArray<string>): string {
	return value.join(',');
}

function parseBypassUserIds(value: string): Array<string> {
	const parsed: Array<string> = [];
	const seen = new Set<string>();

	for (const rawEntry of value.split(',')) {
		const entry = rawEntry.trim();
		if (entry.length === 0 || seen.has(entry)) {
			continue;
		}
		seen.add(entry);
		parsed.push(entry);
	}

	return parsed;
}

export const GeneralTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const toggleGroups = getToggleGroups();
	const currentUser = UserStore.currentUser;
	const canConfigureMentionSuppression = currentUser?.isStaff() ?? false;
	const suppressUnprivilegedSelfMentions = UserSettingsStore.getSuppressUnprivilegedSelfMentions();
	const storedBypassUserIds = formatBypassUserIds(UserSettingsStore.getSuppressUnprivilegedSelfMentionBypassUserIds());
	const [bypassUserIds, setBypassUserIds] = useState(storedBypassUserIds);

	useEffect(() => {
		setBypassUserIds(storedBypassUserIds);
	}, [storedBypassUserIds]);

	const handleBypassUserIdsBlur = useCallback(async () => {
		if (bypassUserIds === storedBypassUserIds) {
			return;
		}

		try {
			await UserSettingsActionCreators.update({
				suppressUnprivilegedSelfMentionBypassUserIds: parseBypassUserIds(bypassUserIds),
			});
		} catch {
			setBypassUserIds(storedBypassUserIds);
		}
	}, [bypassUserIds, storedBypassUserIds]);

	return (
		<>
			{toggleGroups.map((group, gi) => (
				<div
					key={group.title.id ?? `toggle-group-${gi}`}
					className={gi > 0 ? styles.toggleGroup : styles.toggleGroupFirst}
				>
					<div className={styles.groupTitle}>{t(group.title)}</div>
					<div className={styles.toggleList}>
						{group.items.map(({key, label, description}) => (
							<Switch
								key={String(key)}
								label={t(label)}
								description={description ? t(description) : undefined}
								value={Boolean(DeveloperOptionsStore[key])}
								onChange={(value) => {
									DeveloperOptionsActionCreators.updateOption(key, value);
									if (key === 'selfHostedModeOverride') {
										window.location.reload();
									}
								}}
							/>
						))}
					</div>
				</div>
			))}
			{canConfigureMentionSuppression && (
				<div
					className={toggleGroups.length > 0 ? styles.toggleGroup : styles.toggleGroupFirst}
				>
					<div className={styles.groupTitle}>{t`Mention Controls`}</div>
					<div className={styles.toggleList}>
						<Switch
							label={t`Suppress Unprivileged Self-Mentions`}
							description={t`When enabled, direct mentions and reply mentions from non-bot users without staff or CTP status will not actually mention you unless they are allowlisted below.`}
							value={suppressUnprivilegedSelfMentions}
							onChange={(value) => {
								void UserSettingsActionCreators.update({suppressUnprivilegedSelfMentions: value});
							}}
						/>
						<Input
							label={t`Bypass User IDs`}
							placeholder={t`123,456,789`}
							value={bypassUserIds}
							onChange={(event) => setBypassUserIds(event.target.value)}
							onBlur={() => {
								void handleBypassUserIdsBlur();
							}}
							onKeyDown={(event) => {
								if (isIMEComposing(event)) {
									return;
								}
								if (event.key === 'Enter') {
									event.currentTarget.blur();
								}
							}}
							footer={t`Comma-separated user IDs that may still mention you while suppression is enabled.`}
						/>
					</div>
				</div>
			)}
		</>
	);
});
